// Shift Monitor -- surfaces sites whose POS day-end (last shift) is behind.
// "Last shift" = xsiteio_last_shift_at, synced from xsiteio every 4h (cron)
// or on demand via the "Sync now" button here (POST /api/xsiteio/sync, the
// same full sync the Customers page uses). A site is BEHIND when its last
// shift is 2+ days old (missed a day-end): on Aug 4, an Aug 3 shift is fine
// but Aug 2 or older is behind. Filter by state / POS / fuel brand; breakdown
// cards show which states and site types are worst. CSV honours the filter.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const isBehind = (r) => r.shift_state === 'behind' || r.shift_state === 'never';

function badge(bg, fg) {
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: '999px',
    fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, background: bg, color: fg,
  };
}

function StateBadge({ state, daysBehind }) {
  if (state === 'behind')  return <span style={badge('#fee2e2', '#b91c1c')}>{daysBehind}d behind</span>;
  if (state === 'never')   return <span style={badge('#fef3c7', '#92400e')}>no shift ever</span>;
  if (state === 'current') return <span style={badge('#dcfce7', '#166534')}>current</span>;
  return <span style={badge('#e5e7eb', '#4b5563')}>no xsiteio link</span>;
}

function Tile({ label, value, tone }) {
  return (
    <div style={{ flex: '1 1 130px', minWidth: 130, padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface)' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone || 'var(--color-text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>{label}</div>
    </div>
  );
}

function groupBy(rows, key) {
  const m = new Map();
  for (const r of rows) {
    const k = r[key] || '—';
    if (!m.has(k)) m.set(k, { key: k, behind: 0, total: 0 });
    const e = m.get(k);
    e.total += 1;
    if (isBehind(r)) e.behind += 1;
  }
  return [...m.values()].sort((a, b) => b.behind - a.behind || b.total - a.total || String(a.key).localeCompare(String(b.key)));
}

function BreakdownCard({ title, data }) {
  return (
    <div style={{ flex: '1 1 240px', minWidth: 240, border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface)', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid var(--color-border)' }}>{title} <span style={{ color: 'var(--color-text-dim)', fontWeight: 400 }}>(behind / total)</span></div>
      <div style={{ maxHeight: 240, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <tbody>
            {data.map((d) => (
              <tr key={d.key} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '4px 12px' }}>{d.key}</td>
                <td style={{ padding: '4px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                  <span style={{ color: d.behind ? '#b91c1c' : 'var(--color-text-dim)', fontWeight: d.behind ? 600 : 400 }}>{d.behind}</span>
                  <span style={{ color: 'var(--color-text-dim)' }}> / {d.total}</span>
                </td>
              </tr>
            ))}
            {data.length === 0 && <tr><td style={{ padding: 12, color: 'var(--color-text-dim)' }}>No data.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function toCsv(rows) {
  const esc = (v) => {
    if (v == null) return '';
    const str = String(v);
    return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const headers = ['Customer', 'Site', 'Site #', 'Province', 'POS', 'Fuel Brand', 'Last Shift', 'Days Behind', 'Shift State'];
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push([
      r.company, r.site_name, r.site_number, r.province_state, r.pos_type, r.fuel_brand,
      r.last_shift_date || 'never', r.days_behind == null ? '' : r.days_behind, r.shift_state,
    ].map(esc).join(','));
  }
  return '﻿' + lines.join('\r\n');
}

export default function ShiftMonitor() {
  const [rows, setRows]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [behindOnly, setBehindOnly] = useState(true);
  const [fState, setFState]   = useState('');
  const [fPos, setFPos]       = useState('');
  const [fBrand, setFBrand]   = useState('');

  async function load({ initial = false } = {}) {
    if (initial) setLoading(true);
    setError(null);
    try {
      const r = await axios.get('/api/reports/shift-status');
      setRows(r.data.rows || []);
      setSummary(r.data.summary || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      if (initial) setLoading(false);
    }
  }

  useEffect(() => { load({ initial: true }); }, []);

  async function syncNow() {
    setSyncing(true); setSyncMsg(null); setError(null);
    try {
      const r = await axios.post('/api/xsiteio/sync');
      const s = r.data || {};
      const parts = [];
      if (s.updated != null)   parts.push(`${s.updated} updated`);
      if (s.matched != null)   parts.push(`${s.matched} matched`);
      if (s.unmatched)         parts.push(`${s.unmatched} unmatched`);
      setSyncMsg(parts.length ? `Synced — ${parts.join(', ')}.` : 'Synced.');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSyncing(false);
    }
  }

  const opts = useMemo(() => {
    const uniq = (k) => [...new Set(rows.map((r) => r[k]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
    return { states: uniq('province_state'), pos: uniq('pos_type'), brands: uniq('fuel_brand') };
  }, [rows]);

  const byState = useMemo(() => groupBy(rows, 'province_state'), [rows]);
  const byPos   = useMemo(() => groupBy(rows, 'pos_type'), [rows]);
  const byBrand = useMemo(() => groupBy(rows, 'fuel_brand'), [rows]);

  const shown = useMemo(() => rows.filter((r) => {
    if (behindOnly && !isBehind(r)) return false;
    if (fState && (r.province_state || '') !== fState) return false;
    if (fPos && (r.pos_type || '') !== fPos) return false;
    if (fBrand && (r.fuel_brand || '') !== fBrand) return false;
    return true;
  }), [rows, behindOnly, fState, fPos, fBrand]);

  function downloadCsv() {
    const blob = new Blob([toCsv(shown)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shift-status-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  const selStyle = { padding: '4px 8px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' };

  if (loading) return <section className="page"><h2>Shift Monitor</h2><p>Loading…</p></section>;
  if (error && !rows.length) return <section className="page"><h2>Shift Monitor</h2><p style={{ color: 'var(--color-bad)' }}>Error: {error}</p></section>;

  return (
    <section className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <div>
          <h2 style={{ margin: 0 }}>Shift Monitor</h2>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
            Sites whose POS day-end (last shift) is behind. Behind = last shift 2+ days old (missed a day-end). Auto-syncs from xsiteio every 4 hours.
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <button className="btn-primary btn-sm" onClick={syncNow} disabled={syncing}>
            {syncing ? 'Syncing xsiteio…' : 'Sync now'}
          </button>
          {syncMsg && <span style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>{syncMsg}</span>}
        </div>
      </div>

      {summary && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
          <Tile label="Behind (missed a day-end)" value={summary.behind} tone="#b91c1c" />
          <Tile label="No shift ever" value={summary.never} tone="#92400e" />
          <Tile label="Current" value={summary.current} tone="#166534" />
          <Tile label="No xsiteio link" value={summary.no_link} tone="var(--color-text-dim)" />
          <Tile label="Active sites total" value={summary.total} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <BreakdownCard title="Behind by Province" data={byState} />
        <BreakdownCard title="Behind by POS type" data={byPos} />
        <BreakdownCard title="Behind by Fuel Brand" data={byBrand} />
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={behindOnly} onChange={(e) => setBehindOnly(e.target.checked)} />
          Behind only
        </label>
        <select style={selStyle} value={fState} onChange={(e) => setFState(e.target.value)}>
          <option value="">All provinces</option>
          {opts.states.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select style={selStyle} value={fPos} onChange={(e) => setFPos(e.target.value)}>
          <option value="">All POS types</option>
          {opts.pos.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select style={selStyle} value={fBrand} onChange={(e) => setFBrand(e.target.value)}>
          <option value="">All fuel brands</option>
          {opts.brands.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <button className="btn-secondary btn-sm" onClick={downloadCsv}>Download CSV</button>
        <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>{shown.length} shown</span>
      </div>

      <div style={{ overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--color-surface-sunken, #f8fafc)' }}>
            <tr>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Customer</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Site</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Province</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>POS</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Fuel Brand</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Last Shift</th>
              <th style={{ padding: '6px 10px', textAlign: 'left' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.site_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 10px' }}>{s.company}</td>
                <td style={{ padding: '6px 10px' }}>
                  {s.site_name}{' '}
                  <Link to={`/customers/${s.customer_id}?tab=sites&site_number=${encodeURIComponent(s.site_number || '')}`} style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text-dim)', fontSize: 11 }}>{s.site_number}</Link>
                </td>
                <td style={{ padding: '6px 10px' }}>{s.province_state || '—'}</td>
                <td style={{ padding: '6px 10px' }}>{s.pos_type || '—'}</td>
                <td style={{ padding: '6px 10px' }}>{s.fuel_brand || '—'}</td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{s.last_shift_date || 'never'}</td>
                <td style={{ padding: '6px 10px' }}><StateBadge state={s.shift_state} daysBehind={s.days_behind} /></td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-dim)' }}>No sites match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
